import Fuse from 'fuse.js'

// Shared Unicode text normalization + tokenization for the local search engines
// (Fuse + MiniSearch). This is the single source of truth so the index side and
// the query side stay byte-for-byte consistent across every engine.
//
// Why this exists
// ---------------
// The previous tokenizers split on `\W+`, where `\W` is ASCII-only
// (`[^A-Za-z0-9_]`). Every accented Latin letter (ěščř, łąę, éàç) and every
// CJK / Thai / Indic character was treated as a separator, so non-ASCII words
// were shredded into fragments or dropped entirely and became unsearchable.
// Algolia (the remote display engine) already folds diacritics and segments
// CJK server-side, so this layer only has to bring the LOCAL engines up to par.
//
// Two concerns, handled separately:
//   1. Folding   — accent-insensitive matching (matches Algolia). Done with
//                  NFKD + stripping ONLY the Latin/Greek/Cyrillic combining
//                  diacritic ranges, so essential combining marks in Indic
//                  (matras), Thai vowels, and Arabic harakat survive.
//   2. Tokenizing — spaced scripts word-tokenize cleanly; no-space scripts
//                  (CJK, Thai, Khmer, Lao, Myanmar, Tibetan) are segmented with
//                  Intl.Segmenter when available, falling back to unigram +
//                  bigram + full-run tokens otherwise.

// Combining-diacritic ranges that are safe to strip for accent-insensitive
// folding: Combining Diacritical Marks (U+0300–U+036F), Combining Diacritical
// Marks Extended (U+1AB0–U+1AFF), Supplement (U+1DC0–U+1DFF), and for Symbols
// (U+20D0–U+20FF). These cover Latin/Greek/Cyrillic accents (and the marks that
// NFKD-decomposing Arabic alef-hamza forms produce) without touching the
// semantically essential combining marks used by Indic / Thai / Arabic text.
// Whether a code point is a Latin/Greek/Cyrillic combining diacritic that is
// safe to strip for accent-insensitive folding. Checked by code point (rather
// than a regex character class, which would either trip
// no-misleading-character-class or, via \p{Mn}, also strip the essential
// combining marks of Indic / Thai / Arabic text).
function isStrippableDiacritic(cp) {
  return (
    (cp >= 0x0300 && cp <= 0x036F) || // Combining Diacritical Marks
    (cp >= 0x1AB0 && cp <= 0x1AFF) || // Combining Diacritical Marks Extended
    (cp >= 0x1DC0 && cp <= 0x1DFF) || // Combining Diacritical Marks Supplement
    (cp >= 0x20D0 && cp <= 0x20FF) //    Combining Diacritical Marks for Symbols
  )
}

function stripDiacritics(str) {
  let out = ''
  for (let i = 0; i < str.length; i += 1) {
    if (!isStrippableDiacritic(str.charCodeAt(i))) out += str[i]
  }
  return out
}

// Letters that do not NFKD-decompose into a base letter + combining mark and so
// must be folded explicitly. Keys are lower-cased (folding lower-cases first).
const SPECIAL_FOLD = {
  ł: 'l',
  đ: 'd',
  ø: 'o',
  ß: 'ss',
  æ: 'ae',
  œ: 'oe',
  þ: 'th',
  ð: 'd',
  ı: 'i',
  ŀ: 'l',
}

// Built from the map keys (rather than a regex literal) to avoid the
// no-misleading-character-class lint rule and stay in sync with SPECIAL_FOLD.
const SPECIAL_FOLD_REGEX = new RegExp(`[${Object.keys(SPECIAL_FOLD).join('')}]`, 'g')

/**
 * Fold a string for accent-insensitive matching: NFKD decompose, strip
 * Latin/Greek/Cyrillic combining diacritics, lower-case, then map the handful
 * of non-decomposing special letters. ASCII input is effectively just
 * lower-cased.
 *
 * @param {string} str
 * @returns {string}
 */
export function foldText(str) {
  if (typeof str !== 'string' || str.length === 0) return ''
  return stripDiacritics(str.normalize('NFKD'))
    .toLowerCase()
    .replace(SPECIAL_FOLD_REGEX, (c) => SPECIAL_FOLD[c] || c)
}

// Scripts that do not delimit words with spaces — they need segmentation or
// n-gram tokenization to be searchable by individual words. Covers
// Hiragana/Katakana, CJK ideographs (incl. Extension A + compatibility),
// half-width Kana, Hangul, Thai, Lao, Khmer, Myanmar, and Tibetan.
const NO_SPACE_REGEX = /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\uff66-\uff9f\uac00-\ud7af\u0e00-\u0eff\u1000-\u109f\u1780-\u17ff\u0f00-\u0fff]/

// Split spaced scripts on any run of non-(letter/number/underscore). Underscore
// is kept as a word character to preserve the previous `\W` behaviour.
const WORD_SPLIT_REGEX = /[^\p{L}\p{N}_]+/u

// Lazily-created, memoised word segmenter. `undefined` locale lets the runtime
// pick its best dictionaries (ICU handles JP/ZH/TH/KM/LO/MY word breaking).
let wordSegmenter
let segmenterResolved = false

function getWordSegmenter() {
  if (segmenterResolved) return wordSegmenter
  segmenterResolved = true
  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    try {
      wordSegmenter = new Intl.Segmenter(undefined, { granularity: 'word' })
    } catch (e) {
      wordSegmenter = null
    }
  } else {
    wordSegmenter = null
  }
  return wordSegmenter
}

// Push a token only if it is non-empty and not already emitted for this run.
function pushUnique(out, seen, token) {
  if (token && token.length > 0 && !seen.has(token)) {
    seen.add(token)
    out.push(token)
  }
}

// Emit every unigram + adjacent bigram, plus the full run, so both Fuse
// (substring matching against the token array) and MiniSearch (per-term
// matching) can resolve arbitrary CJK substrings that cross word boundaries.
// `Array.from` keeps surrogate pairs intact.
function ngramFallback(run, out, seen) {
  const chars = Array.from(run)
  for (let i = 0; i < chars.length; i += 1) {
    pushUnique(out, seen, chars[i])
    if (i + 1 < chars.length) pushUnique(out, seen, chars[i] + chars[i + 1])
  }
  if (chars.length > 2) pushUnique(out, seen, run)
}

// Tokenize a no-space-script run into searchable tokens. When Intl.Segmenter is
// available we add real word segments (best precision for whole-word queries),
// and in every case we also add character n-grams + the full run so substring
// queries that cross segmentation boundaries still match — mirroring the
// character-level matching the raw-field (navigation) path already provides.
function segmentNoSpaceRun(run, out) {
  const seen = new Set()
  const segmenter = getWordSegmenter()
  if (segmenter) {
    const segments = segmenter.segment(run)
    Array.from(segments).forEach((seg) => {
      if (seg.isWordLike) pushUnique(out, seen, seg.segment)
    })
  }
  ngramFallback(run, out, seen)
}

/**
 * Tokenize text into folded, searchable tokens. Spaced scripts (Latin,
 * Cyrillic, Greek, Arabic, Hebrew, Indic, …) produce one token per word;
 * no-space scripts produce word segments (or n-grams as a fallback).
 *
 * Used by both the document-indexing path and the query path so matching is
 * symmetric.
 *
 * @param {string} str
 * @returns {Array<string>}
 */
export function tokenize(str) {
  const folded = foldText(str)
  if (!folded) return []
  const runs = folded.split(WORD_SPLIT_REGEX).filter((r) => r.length > 0)
  const tokens = []
  for (let i = 0; i < runs.length; i += 1) {
    const run = runs[i]
    if (NO_SPACE_REGEX.test(run)) {
      segmentNoSpaceRun(run, tokens)
    } else {
      tokens.push(run)
    }
  }
  return tokens
}

/**
 * Flatten a list of strings into a single array of tokens. Mirrors the old
 * `tokenizeMany` helper used by the per-set / per-question accumulators.
 *
 * @param {Array<string>} strings
 * @returns {Array<string>}
 */
export function tokenizeMany(strings) {
  if (!Array.isArray(strings)) return []
  const out = []
  for (let i = 0; i < strings.length; i += 1) {
    const toks = tokenize(strings[i])
    for (let j = 0; j < toks.length; j += 1) out.push(toks[j])
  }
  return out
}

/**
 * Fuse `getFn` that folds extracted field values so raw-field Fuse indices
 * (navigation / external links / quick actions) match accent-insensitively.
 * Folds the query separately at each call site.
 *
 * @param {Object} obj
 * @param {string|string[]} path
 * @returns {string|string[]}
 */
export function foldingGetFn(obj, path) {
  const value = Fuse.config.getFn(obj, path)
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === 'string' ? foldText(v) : v))
  }
  return typeof value === 'string' ? foldText(value) : value
}
